import type {
  TriggerOutputData,
  ValueConfig,
} from "@smartsuite/automation-engine-api";
import { unwrapValueConfig } from "@smartsuite/automation-engine-api";
import { useRootStore } from "../../../../../stores/hooks";
import type React from "react";
import { findInputValue } from "../../../../../utils/inputs";
import { WEBHOOKS_REQUEST_CONTENT } from "../../../../../utils/constants";
import { ActivityDetailsItem } from "../ActivityDetailsItem/ActivityDetailsItem";
import { addEmptyValueIfNeeded } from "../../../ActivityGrid/ActivityGrid.helpers";
import { valueDataToString } from "@smartsuite/automation-conditions";
import { translateIfPresent } from "../../../../../locales/i18n";

interface PropsData {
  id: string;
  label: string;
  value: string;
}

const PROPS_SORT_ORDER = [
  "request-url",
  "http-method",
  "request-content",
  "request-raw-body",
];

const getSortedProps = (props: PropsData[]): PropsData[] => {
  return props.sort((a, b) => {
    const indexA = PROPS_SORT_ORDER.indexOf(a.id);
    const indexB = PROPS_SORT_ORDER.indexOf(b.id);

    if (indexA === -1 && indexB === -1) {
      return 0;
    }
    if (indexA === -1) {
      return 1;
    }
    if (indexB === -1) {
      return -1;
    }
    return indexA - indexB;
  });
};

// example for key value: {"a": "Text", "b": {"c": "Number"}}
const getPropsData = (
  id: string,
  label = "",
  value?: ValueConfig,
): PropsData | undefined => {
  if (value === undefined) {
    return undefined;
  }
  value = unwrapValueConfig(value);
  return value.values.reduce(
    (acc, v, i) => {
      let val = "";
      let startObject = false;
      let endObject = false;
      switch (v.value.oneofKind) {
        case "string":
          val = translateIfPresent("caption.Text", "Text");
          break;
        case "number":
          val = translateIfPresent("caption.Number", "Number");
          break;
        case "bool":
          val = translateIfPresent("caption.Boolean", "Boolean");
          break;
        case "keyValue": {
          startObject = i === 0;
          endObject = i === (value?.values?.length ?? 0) - 1;
          const key =
            v.value.keyValue.key?.values[0]?.value?.oneofKind === "string"
              ? v.value.keyValue.key?.values[0].value.string
              : "";
          let values =
            getPropsData(id, key, v.value.keyValue.value)?.value ?? "";
          values = values.startsWith("{") ? values : `"${values}"`;
          val = `"${key}": ${values}${endObject ? "" : ", "}`;
          break;
        }
        case "list": {
          const items = v.value.list.values
            .map((v) => getPropsData(id, label, v)?.value ?? "")
            .join(", ");
          val = `[${items}]`;
          break;
        }
      }
      return {
        ...acc,
        value: `${startObject ? "{" : ""}${acc.value.concat(val)}${
          endObject ? "}" : ""
        }`,
      };
    },
    { id, label, value: "" },
  );
};

interface WebhookTriggerResultProps {
  props: () => React.ReactNode;
}

export const useWebhookTriggerProps = (
  trigger?: TriggerOutputData,
): WebhookTriggerResultProps => {
  const { automations } = useRootStore();
  if (trigger === undefined) {
    return {
      props: () => {
        return <></>;
      },
    };
  }

  const requestContentInput = findInputValue(
    automations.candidate.self.trigger.inputs,
    WEBHOOKS_REQUEST_CONTENT,
  );
  const propsData: PropsData[] = [];

  if (requestContentInput !== undefined) {
    propsData.push({
      id: WEBHOOKS_REQUEST_CONTENT,
      label: translateIfPresent("caption.Request Content", "Request Content"),
      value:
        getPropsData(WEBHOOKS_REQUEST_CONTENT, "", requestContentInput)
          ?.value ?? "",
    });
  }

  propsData.push(
    ...trigger.outputs.map((output) => ({
      id: output.outputId,
      label: output.outputLabel,
      value: valueDataToString(output.value),
    })),
  );

  return {
    props: () => {
      return getSortedProps(propsData).map((input) => (
        <ActivityDetailsItem key={input.id} label={input.label}>
          {addEmptyValueIfNeeded(input.value)}
        </ActivityDetailsItem>
      ));
    },
  };
};
